import { formatShortDateTime } from "@clipboard-health/date-time";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import {
  CbhIcon,
  Illustration,
  LoadingSpinner,
  Text,
} from "@src/appV2/redesign/system/ui-components";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { InView } from "react-intersection-observer";

import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

interface ListWorkplaceInterviewFeedbackContentProps {
  interviews: InterviewWithDetails[];
  isFetchingNextPageInterviews?: boolean;
  hasNextPageInterview?: boolean;
  fetchNextPageInterviews: () => Promise<unknown>;
  onInterviewSelected: (interview: InterviewWithDetails) => void;
  placementCandidate: PlacementCandidate;
}

export function ListWorkplaceInterviewFeedbackContent(
  props: ListWorkplaceInterviewFeedbackContentProps
) {
  const {
    fetchNextPageInterviews,
    isFetchingNextPageInterviews,
    hasNextPageInterview,
    interviews,
    onInterviewSelected,
    placementCandidate,
  } = props;

  const source = useInterviewAttendanceSource();

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACES_VIEWED, {
    placementCandidate,
    interviews,
    ...(interviews && { interviewsCount: interviews.length }),
    source,
  });

  return (
    <Stack
      spacing={8}
      alignItems="center"
      justifyContent="center"
      sx={{ paddingX: 8, paddingY: 6 }}
    >
      <Illustration type="chat" />
      <Stack spacing={6} textAlign="center">
        <Text variant="h2">Tell us how your interview went</Text>
        <Text>Let us know what happened so we can keep things moving.</Text>
        <Stack>
          <Stack spacing={3} width="100%">
            {interviews.map((interview) => (
              <Button
                key={interview.id}
                variant="outlined"
                size="large"
                sx={{
                  svg: {
                    width: "1.75rem",
                    height: "1.75rem",
                  },
                  height: "auto",
                  py: 5,
                }}
                onClick={() => {
                  logEvent(
                    APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACES_OPTION_CLICKED,
                    {
                      placementCandidate,
                      interview,
                      source,
                    }
                  );
                  onInterviewSelected(interview);
                }}
              >
                <Stack
                  direction="row"
                  flex={1}
                  spacing={4}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={5} alignItems="center">
                    <CbhIcon type="building" size="xLarge" />
                    <Stack spacing={3} textAlign="left">
                      <Text>{interview.workplace?.name}</Text>
                      <Text sx={{ color: (theme) => theme.palette.text.secondary }} variant="body2">
                        Interviewed {formatShortDateTime(interview.start)}
                      </Text>
                    </Stack>
                  </Stack>
                  <CbhIcon
                    type="chevron-right"
                    sx={{
                      width: "1.25rem !important",
                      height: "1.25rem !important",
                    }}
                  />
                </Stack>
              </Button>
            ))}
          </Stack>
        </Stack>
      </Stack>

      {hasNextPageInterview ? (
        <Stack direction="row" justifyContent="center">
          <Box sx={{ marginTop: 4, marginBottom: 10 }}>
            <InView
              as="div"
              onChange={async (inView) => {
                if (inView && !isFetchingNextPageInterviews) {
                  await fetchNextPageInterviews();
                }
              }}
            />
            <Box sx={{ opacity: isFetchingNextPageInterviews ? 1 : 0 }}>
              <LoadingSpinner size="medium" />
            </Box>
          </Box>
        </Stack>
      ) : undefined}
    </Stack>
  );
}
